import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Tag, Tooltip } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import {
  fetchBenchmarkCoverageDiagnostics,
  fetchBenchmarkCoverageSummary,
  fetchEligibleBenchmarkOptions,
  fetchMacroBackground,
  HISTORICAL_SCENARIO,
  recomputeSessionBenchmarkCoverage,
  retrieveUnderlyingAssets,
  selectors,
  updateSessionMarketsView,
} from 'state';

import '../../../TrainerSession/SessionSettingsShared/settings_studio.css';
import '../../../TrainerSession/SessionSettingsDense/dense.css';
import './blueprintSettingsV2.css';
import {
  isSessionLocked,
  useFillHeight,
} from '../../../TrainerSession/SessionSettingsShared/helpers';
import { useBenchmarkEditOverride } from '../../../TrainerSession/SessionSettingsShared/useBenchmarkEditOverride';
import { SettingsLockNudgeProvider } from '../../../TrainerSession/SessionSettingsShared/SettingsLockNudge';
import SettingsCard from '../../../TrainerSession/SessionSettingsShared/components/SettingsCard';
import { TrainerSessionContext } from '../../../TrainerSession/TrainerSessionContext';
import { InstrumentSelectionContext } from '../../../TrainerSession/SessionSettings/instrumentSelectionContext';
import BenchmarkViewV2 from '../../../TrainerSession/SessionSettingsDense/benchmark_v2/BenchmarkViewV2';
import GeneralViewV2 from '../../../TrainerSession/SessionSettingsDense/general_v2/GeneralViewV2';
import InstrumentsPanelV2 from '../../../../features/assetCatalogue/SessionInstrumentsSwitch';
import { SessionCatalogueProvider } from '../../../../features/assetCatalogue/SessionCatalogue';
import MacroBackground from '../../../TrainerSession/MacroBackground';
import BlueprintRailV2, { BlueprintDenseView } from './BlueprintRailV2';
import BlueprintToolsView from './BlueprintToolsView';
import BlueprintNewsView from './BlueprintNewsView';
import BlueprintDisplayNames from './BlueprintDisplayNames';

type Props = {
  blueprintId: number;
  blueprintName: string;
  difficultyLabel: string;
  versionNumber?: number;
  sessionId: number;
  variantId: number;
  readOnly?: boolean;
  onOpenDiagnostics: () => void;
  assessmentStatus?: React.ReactNode;
};

const InstrumentsView: React.FC<{ readOnly?: boolean }> = ({ readOnly }) => (
  <div className="set3b-instruments">
    <InstrumentsPanelV2 readOnly={readOnly} />
  </div>
);

const MacroView: React.FC = () => (
  <div className="set3b-macro">
    <SettingsCard title="Macro Background" scroll>
      <MacroBackground />
    </SettingsCard>
  </div>
);

const BenchmarkView: React.FC<{ historical: boolean }> = ({ historical }) => {
  const session = useSelector(selectors.activeSession);
  const benchmarkEditOverride = useBenchmarkEditOverride();
  const benchmarkLocked = isSessionLocked(session) && !benchmarkEditOverride;
  return <BenchmarkViewV2 historical={historical} locked={benchmarkLocked} />;
};

const BlueprintTopBar: React.FC<
  Pick<
    Props,
    | 'blueprintId'
    | 'blueprintName'
    | 'difficultyLabel'
    | 'versionNumber'
    | 'readOnly'
    | 'assessmentStatus'
  >
> = ({
  blueprintId,
  blueprintName,
  difficultyLabel,
  versionNumber,
  readOnly,
  assessmentStatus,
}) => {
  const session = useSelector(selectors.activeSession);
  const currencies = useSelector(selectors.currencies);
  const currency = currencies?.[session.reporting_currency]?.symbol;
  const historical = session.scenario_type === HISTORICAL_SCENARIO;

  return (
    <div className="set-studio__topbar bp-settings-v2__topbar">
      <div className="bp-settings-v2__heading">
        <Link
          className="bp-settings-v2__back"
          to={`/dashboard/blueprints/${blueprintId}`}
        >
          ← Back to blueprint
        </Link>
        <div className="set-studio__brand">
          <Tooltip title={blueprintName}>
            <span className="set-studio__brand-title">
              {blueprintName} · {difficultyLabel} · v{versionNumber}
            </span>
          </Tooltip>
          <span className="set-studio__brand-meta">
            <Tag color="blue" style={{ marginInlineEnd: 0 }}>
              Creating
            </Tag>
            <span>·</span>
            <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
              {historical ? 'Historical' : 'Real Time'}
            </Tag>
            {currency ? (
              <>
                <span>·</span>
                {currency}
              </>
            ) : null}
          </span>
        </div>
      </div>
      <div className="set-studio__topbar-right bp-settings-v2__topbar-right">
        {assessmentStatus}
        {readOnly ? (
          <Tooltip title="This version is frozen. Create a new version to change its configuration.">
            <span className="set-studio__lockchip set-studio__lockchip--locked">
              <LockOutlined />
              Version locked
            </span>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Blueprint-specific V2 copy of SessionSettingsDense. The data/actions remain
 * identical, while this shell owns the blueprint header, tools view and scoped
 * overflow behavior.
 */
const BlueprintSettingsV2: React.FC<Props> = props => {
  const dispatch = useDispatch();
  const { ref, height } = useFillHeight();
  useContext(TrainerSessionContext);

  const session = useSelector(selectors.activeSession);
  const historical = session.scenario_type === HISTORICAL_SCENARIO;
  const [view, setView] = useState<BlueprintDenseView>('general');

  const [selectedInstruments, setSelectedInstruments] = useState<number[]>(
    session.underlying_assets ?? [],
  );
  useEffect(() => {
    setSelectedInstruments(session.underlying_assets ?? []);
  }, [session.underlying_assets]);
  const instrumentSelection = useMemo(
    () => ({
      selected: selectedInstruments,
      setSelected: setSelectedInstruments,
    }),
    [selectedInstruments],
  );

  useEffect(() => {
    dispatch(retrieveUnderlyingAssets({}));
  }, [dispatch]);
  useEffect(() => {
    if (!session?.id) return;
    dispatch(fetchEligibleBenchmarkOptions({ session_id: session.id }));
  }, [dispatch, session?.id]);
  useEffect(() => {
    if (!session?.id || session.fetched_macro_background) return;
    dispatch(fetchMacroBackground({ session_id: session.id }));
  }, [dispatch, session?.id, session?.fetched_macro_background]);

  const panelReadOnly =
    props.readOnly &&
    view !== 'instruments' &&
    view !== 'blueprint' &&
    view !== 'news';

  return (
    <SessionCatalogueProvider>
      <InstrumentSelectionContext.Provider value={instrumentSelection}>
        <SettingsLockNudgeProvider>
          <div
            ref={ref}
            className="set-studio set3 bp-settings-v2"
            style={{ height }}
          >
            <BlueprintTopBar {...props} />
            <div className="set-studio__body">
              <BlueprintRailV2
                active={view}
                onSelect={setView}
                session={session}
              />
              <div className="set-studio__workspace">
                <div
                  className="set-studio__panel"
                  style={
                    panelReadOnly
                      ? { pointerEvents: 'none', opacity: 0.6 }
                      : undefined
                  }
                  aria-disabled={panelReadOnly || undefined}
                >
                  {view === 'general' ? (
                    <GeneralViewV2 blueprintMode />
                  ) : view === 'blueprint' ? (
                    <BlueprintToolsView
                      displayNames={
                        <BlueprintDisplayNames
                          key={props.blueprintId}
                          blueprintId={props.blueprintId}
                        />
                      }
                      sessionId={props.sessionId}
                      whichMarkets={session.which_markets ?? 0}
                      readOnly={props.readOnly}
                      onOpenDiagnostics={props.onOpenDiagnostics}
                      historical={historical}
                      coverageActionWaiting={
                        historical
                          ? session.eligible_benchmark_options_waiting
                          : session.benchmark_coverage_diagnostics_waiting
                      }
                      onCoverageAction={() => {
                        if (historical) {
                          dispatch(
                            recomputeSessionBenchmarkCoverage({
                              session_id: props.sessionId,
                            }),
                          );
                          return;
                        }
                        dispatch(
                          fetchBenchmarkCoverageSummary({
                            session_id: props.sessionId,
                          }),
                        );
                        dispatch(
                          fetchBenchmarkCoverageDiagnostics({
                            session_id: props.sessionId,
                          }),
                        );
                      }}
                      onWhichMarketsChange={whichMarkets =>
                        dispatch(
                          updateSessionMarketsView({
                            session_id: props.sessionId,
                            which_markets: whichMarkets,
                          }),
                        )
                      }
                    />
                  ) : view === 'news' ? (
                    <BlueprintNewsView
                      key={`${props.blueprintId}:${props.variantId}`}
                      blueprintId={props.blueprintId}
                      variantId={props.variantId}
                      readOnly={props.readOnly}
                    />
                  ) : view === 'instruments' ? (
                    <InstrumentsView readOnly={props.readOnly} />
                  ) : view === 'macro' ? (
                    <MacroView />
                  ) : (
                    <BenchmarkView historical={historical} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </SettingsLockNudgeProvider>
      </InstrumentSelectionContext.Provider>
    </SessionCatalogueProvider>
  );
};

export default BlueprintSettingsV2;
